/**
 * 이상한 방 - 릴이 도는 동안의 소리 ("무엇이 언제 울리는가")
 *
 * 근거 문서: 로드맵 확정안 3.3절(연출 시퀀스 · 소리는 전부 합성) · 0절 판정표 10번 · 6절("근접 실패" 기각)
 *           `ui/juice.ts` 머리말(정리 권한은 타이머 하나)
 *
 * `envelopePhases.ts`에서 갈라져 나왔다. 릴이 2.6초를 도는 동안 열몇 개의 소리가 울리는데, 그 일정을 짜는 일과
 * 하나씩 이어 거는 일이 단계 전이 한가운데에 있으면 "언제 어느 단계로 가는가"가 소리 커서에 묻힌다.
 *
 * ## 네 등급이 완전히 같은 소리를 듣는다
 *
 * 이 파일에는 `Grade`가 없다. 똑딱의 시각은 실제 착지값이 아니라 **명목 이동 거리**에서 나오고
 * (`reelTickTimes` - `envelopeReel.ts` 머리말), 차오름의 자리는 소리 길이에서만 나온다. 그래서 소리 채널로는
 * 개수도 간격도 등급을 말하지 않는다 - 눈에 보이는 릴은 정직해도 되지만(멎은 자리가 곧 `gradeRoll`이다)
 * 같은 값을 귀로 한 번 더 흘릴 이유는 없고, 흘리기 시작하면 "개봉 전은 네 등급이 같다"가 깨진다.
 *
 * ## 차오름이 뒤로 물러난 이유
 *
 * 전에는 `envelopeCharge`(반음씩 오르는 틱 일곱)가 개봉 전의 맨 앞에서 울렸다. 릴이 2.6초가 되자 그 자리의
 * 오르는 음은 "이제 시작한다"였지 "곧 멎는다"가 아니었다. 그래서 **릴이 멎는 순간에 끝나도록** 역산해서
 * 앉힌다 - 똑딱의 간격이 벌어지는 동안 음이 올라가고, 마지막 틱과 정지가 같은 순간에 온다.
 *
 * 길이는 `envelopeChargeSec()`에 물어본다. 710을 손으로 적으면 소리를 고친 날 화면이 조용히 어긋난다.
 *
 * ## 착지에 똑딱 하나가 겹친다
 *
 * 경계 똑딱은 마지막 경계를 지나면 끝난다(릴 시간의 71~76%. 바퀴 수가 정한다). 그 뒤로 차오름만 흐르다 소리가 그냥
 * 사라지니 릴이 "멎었다"가 아니라 "끝났다"로 들렸고, 이어지는 정지 구간이 침묵이라 더 그랬다. 그래서
 * `spinMs` 정각에 똑딱을 하나 더 앉힌다 - 차오름의 마지막 음과 같은 순간에 물체가 닿는 소리다.
 * 시각이 언제나 `spinMs`라 등급을 흘리지 않는다(그것이 이 파일에 `Grade`가 없다는 규약의 전부다).
 *
 * ## 소리 커서는 단계를 끌지 않는다
 *
 * 똑딱 열몇 개를 한꺼번에 걸지 않는다. **하나가 울린 뒤 다음 하나를 건다**(`enterStack`과 같은 꼴).
 * 그리고 그 `schedule`은 이 파일이 만들지 않고 **밖에서 받는다** - `setTimeout`이 `envelopePhases.ts` 한 곳에만
 * 있어야 정리 권한이 한 곳에 남고, 화면을 접는 것도 그 타이머를 걷는 것으로 끝난다.
 *
 * **끝을 알리지 않는다**(전에는 `onEnd`를 받아 마지막 소리 뒤에 정지 단계를 불렀다). 그러면 릴의 페이즈 전환이
 * 열몇 개짜리 예약 사슬의 끝에 매달리는데, 브라우저는 **숨은 탭에서 `setTimeout`을 1초에 하나로 묶는다** -
 * 사슬이 열넷이면 2.6초 릴이 십수 초가 되고, 사용자는 이미 멎은 릴 앞에서 카드가 뒤집히기를 기다린다.
 * 전경에서도 예약 하나마다 몇 ms씩 밀려 릴이 명목보다 40ms 길었다. 지금 정지로 넘어가는 것은
 * `envelopePhases.ts`가 `spinMs`에 따로 건 **절대 시각의 예약 하나**이고, 이 파일은 소리만 친다.
 *
 * ## 늦게 온 예약은 밀린 소리를 몰아치지 않는다
 *
 * 그래서 다음 예약까지의 간격을 **시계로 다시 잰다**(`now`도 밖에서 받는다 - 이 파일에는 시계가 없다).
 * 이미 지나간 똑딱은 버린다. 탭이 뒤로 갔다 돌아왔을 때 밀린 열몇 개를 한꺼번에 치면 그건 릴이 아니라 잡음이고,
 * 그 사이 단계는 절대 시각으로 이미 지나가 있어 울릴 자리도 없다.
 */

import { envelopeChargeSec, type EnvelopeSoundName } from '../audio/sounds'
import { MS_PER_SECOND } from '../core/time'
import type { EnvelopeTable } from '../game/economy/envelopeTable'

import { REEL, reelTickTimes } from './envelopeReel'

/* --- 타입 ---------------------------------------------------------------------- */

/** 릴이 도는 동안 울릴 소리 하나. `atMs`는 릴이 출발한 순간 기준의 절대 시각이다. */
export type ReelSound = Readonly<{ atMs: number; sound: EnvelopeSoundName }>

export type ReelSoundRun = Readonly<{
  /**
   * 일정을 짜고 첫 소리를 건다. 다 지나가면 아무것도 부르지 않고 조용히 끝난다 - **단계 전이는 이 커서에
   * 매달려 있지 않다**(머리말). 다시 부르면 앞의 일정은 버려진다(예약을 지우는 것은 `schedule`의 몫이다).
   */
  start: (spinMs: number, seq: number, table: EnvelopeTable) => void
}>

export type ReelSoundDeps = Readonly<{
  play: (name: EnvelopeSoundName) => void
  /** 앞의 **소리** 예약을 지우고 새로 건다. `envelopePhases.ts`의 소리 전용 슬롯이다(단계 슬롯과 갈려 있다). */
  schedule: (run: () => void, ms: number) => void
  /** 지금 몇 시인가. 밖에서 받는 이유는 이 파일이 시계를 갖지 않기 위해서다(`envelopePhases.ts`가 넘긴다). */
  now: () => number
}>

/* --- 순수 계산 ------------------------------------------------------------------ */

/**
 * 릴이 도는 동안의 소리 일정. 세그먼트 경계마다 똑딱이 울리고(감속하므로 간격이 벌어진다),
 * 차오름이 **릴이 멎는 순간에 끝나도록** 뒤에 겹친다.
 *
 * 네 등급이 같은 배열을 받는다(머리말). 그것이 이 함수에 `Grade`가 없는 이유다.
 */
export function reelSoundPlan(spinMs: number, seq: number, table: EnvelopeTable): readonly ReelSound[] {
  if (!(spinMs > 0)) return []
  const chargeMs = Math.round(envelopeChargeSec() * MS_PER_SECOND)
  const ticks = reelTickTimes(spinMs, seq, table)
  const plan: ReelSound[] = ticks.map((atMs) => ({ atMs, sound: 'reelTick' }))
  plan.push({ atMs: Math.max(0, spinMs - chargeMs), sound: 'envelopeCharge' })

  /*
   * **멎는 순간의 '탁' 하나.** 경계 똑딱은 릴 시간의 71~76%에서 끝난다(마지막 경계를 지나고 나면 울릴 것이
   * 없다). 그 뒤로는 차오르는 음만 흐르다가 소리가 그냥 사라졌고, 그래서 릴은 "멎었다"가 아니라
   * "끝났다"로 들렸다. 착지에 똑딱 하나를 겹치면 차오름의 마지막 음과 같은 순간에 물체가 닿는다.
   *
   * 시각은 언제나 `spinMs`라 **등급을 모른다**(이 파일에 `Grade`가 없다는 규약 그대로). 마지막 경계
   * 똑딱과 너무 붙으면 두 번 치는 소리가 되므로 최소 간격 안이면 앉히지 않는다.
   */
  const lastTick = ticks.at(-1)
  if (lastTick === undefined || spinMs - lastTick >= REEL.minTickGapMs) {
    plan.push({ atMs: spinMs, sound: 'reelTick' })
  }

  /*
   * 같은 시각이면 차오름이 먼저다 - 똑딱은 지나가는 표시이고 차오름은 구간이 시작된다는 신호다.
   * 이름이 같으면 0을 돌려준다. `x - y || rank - rank` 대신 삼항으로 적으면 같은 이름 둘에 대해
   * `cmp(a,b)`와 `cmp(b,a)`가 **둘 다 1**이 되어(반대칭 위반) 정렬 순서가 정의되지 않는다.
   * 지금은 최소 간격이 똑딱끼리의 동값을 막아 드러나지 않지만, 그 값을 줄이는 날 조용히 깨진다.
   */
  const rank = (sound: EnvelopeSoundName): number => (sound === 'envelopeCharge' ? 0 : 1)
  return plan.sort((a, b) => a.atMs - b.atMs || rank(a.sound) - rank(b.sound))
}

/* --- 본체 ---------------------------------------------------------------------- */

/** 일정을 하나씩 이어 거는 커서. 상태는 셋뿐이고 전부 `start`가 되돌린다. */
export function createReelSoundRun(deps: ReelSoundDeps): ReelSoundRun {
  const { play, schedule, now } = deps

  let plan: readonly ReelSound[] = []
  let index = 0
  /** 릴이 출발한 시각. 남은 간격을 여기서 다시 재므로 예약이 밀려도 누적되지 않는다. */
  let startedAt = 0

  function step(): void {
    /*
     * **지나간 것은 버린다.** 숨은 탭에서 브라우저가 예약을 1초에 하나로 묶으면 돌아온 순간 밀린 똑딱이
     * 열몇 개다. 그걸 한꺼번에 치면 릴이 아니라 잡음이고, 단계는 절대 시각으로 이미 지나가 있어
     * 그 소리들이 세리머니 위에 겹친다. 최소 간격만큼의 여유를 두는 이유는 한 프레임 늦은 것까지
     * 버리면 정상 재생에서 소리가 빠지기 때문이다.
     */
    while (index < plan.length && (plan[index]?.atMs ?? 0) < now() - startedAt - REEL.minTickGapMs) index += 1
    const next = plan[index]
    if (next === undefined) return
    schedule(() => {
      index += 1
      play(next.sound)
      step()
    }, Math.max(0, next.atMs - (now() - startedAt)))
  }

  return {
    start(spinMs: number, seq: number, table: EnvelopeTable): void {
      plan = reelSoundPlan(spinMs, seq, table)
      index = 0
      startedAt = now()
      step()
    },
  }
}
